import React from 'react';
import {
  Home,
  FolderKanban,
  Search,
  BarChart3,
  Cable,
  Settings,
  FileText,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

const ARCHEN_LOGO =
  'https://customer-assets.emergentagent.com/job_archen-studio/artifacts/kz0rmfk8_logo.svg';

const INTEGRATIONS_IMG =
  'https://customer-assets.emergentagent.com/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/er4h3f8m_image.png';

const ASK_ARCHEN_IMG =
  'https://customer-assets-v7afamib.emergentagent.net/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/wckd5cw6_archen-shot-06-ask-archen.webp';

const steps = [
  {
    num: '01',
    title: 'Connect your tools',
    body: 'Archen™ connects to the places your team already works.',
  },
  {
    num: '02',
    title: 'Archen™ captures what matters',
    body: 'Every decision, approval, and change becomes part of a searchable record, tied back to the email or file it came from.',
  },
  {
    num: '03',
    title: 'Ask your studio what it knows',
    body: 'Find answers, see the context, and reuse knowledge across projects.',
  },
];

const navItems = [
  { icon: Home, label: 'Home' },
  { icon: FolderKanban, label: 'Projects' },
  { icon: Search, label: 'Ask Archen™', active: true },
  { icon: BarChart3, label: 'Insights' },
  { icon: Cable, label: 'Integrations' },
  { icon: Settings, label: 'Settings' },
];

const sources = [
  { title: 'Email from Sarah P.', date: 'May 14, 2024' },
  { title: 'Design Approval Document', date: 'May 14, 2024' },
  { title: 'Material Specification Sheet', date: 'May 13, 2024' },
];

const AskArchenMock = () => (
  <div
    data-testid="ask-archen-mock"
    className="w-full rounded-2xl overflow-hidden border border-white/60 ring-1 ring-black/[0.04] shadow-[0_44px_100px_-30px_rgba(42,32,24,0.32)] bg-[#F3ECE1]/95 backdrop-blur-xl flex"
    style={{ minHeight: '560px' }}
  >
    {/* Sidebar */}
    <div className="w-[170px] flex-shrink-0 bg-[#2A211B] py-5 flex flex-col gap-1">
      <div className="flex items-center justify-between px-5 pb-5 mb-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <img src={ARCHEN_LOGO} alt="Archen™" className="h-5 w-auto invert opacity-90" />
          <span className="text-[#E8DBC6] text-[13px] font-medium">Archen™</span>
        </div>
        <ChevronRight size={13} className="text-white/40" />
      </div>
      {navItems.map(({ icon: Icon, label, active }) => (
        <div
          key={label}
          className={`mx-3 px-3 py-2.5 rounded-lg flex items-center gap-3 ${
            active ? 'bg-white/10' : ''
          }`}
        >
          <Icon size={15} className={active ? 'text-[#E8DBC6]' : 'text-white/45'} />
          <span className={`text-[12.5px] ${active ? 'text-[#E8DBC6] font-medium' : 'text-white/55'}`}>
            {label}
          </span>
        </div>
      ))}
    </div>

    {/* Chat panel */}
    <div className="flex-1 flex flex-col p-6 md:p-7">
      {/* Question bubble */}
      <div className="flex justify-end">
        <div className="bg-[#E4D9C7] rounded-2xl px-5 py-3.5 max-w-[78%] flex items-center gap-4">
          <p className="text-[#3D3229] text-[13px] leading-snug">
            What did the client approve for the kitchen countertops?
          </p>
          <ChevronRight size={15} className="text-[#9C8458] flex-shrink-0" />
        </div>
      </div>

      {/* Answer card */}
      <div className="mt-6 flex gap-3">
        <span className="w-8 h-8 rounded-full bg-white border border-[#E5DDD0] flex items-center justify-center flex-shrink-0">
          <img src={ARCHEN_LOGO} alt="" className="h-4 w-auto" />
        </span>
        <div className="flex-1 bg-white rounded-2xl border border-[#EFE7DA] p-5">
          <p className="text-[#1F1812] text-[14px] leading-relaxed font-medium">
            The client approved the Taj Mahal quartzite slab for the kitchen countertops on May 14, 2024.
          </p>

          <p className="text-[#A69580] text-[11px] mt-5 mb-3">Sources</p>
          <div className="space-y-2.5">
            {sources.map((s) => (
              <div
                key={s.title}
                className="flex items-center gap-3 bg-[#FAF5EE] rounded-xl border border-[#F0E8DB] p-2.5"
              >
                <span className="w-7 h-7 rounded-md bg-white border border-[#EFE7DA] flex items-center justify-center flex-shrink-0">
                  <FileText size={13} className="text-[#8B6D43]" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1F1812] text-[12.5px] font-medium leading-tight truncate">{s.title}</p>
                  <p className="text-[#A69580] text-[11px] mt-0.5">{s.date}</p>
                </div>
                <div className="w-12 h-9 rounded-md bg-gradient-to-br from-[#D8C9B2] to-[#A8957A] flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="mt-auto pt-6">
        <div className="bg-white rounded-2xl border border-[#EFE7DA] px-5 py-3.5 flex items-center justify-between">
          <p className="text-[#A69580] text-[13px]">Ask anything about this project…</p>
          <span className="w-8 h-8 rounded-full bg-[#2A211B] flex items-center justify-center">
            <ArrowRight size={15} className="text-white" />
          </span>
        </div>
      </div>
    </div>
  </div>
);

const CaptureMock = () => (
  <div
    data-testid="capture-mock"
    className="w-full max-w-[460px] mx-auto rounded-2xl overflow-hidden border border-[#E5DDD0] shadow-2xl shadow-black/10 bg-white"
  >
    <div className="px-6 py-4 border-b border-[#F0E6D8] flex items-center justify-between">
      <div>
        <p className="text-[#A69580] text-[10px] tracking-[0.22em] uppercase">Project</p>
        <p
          className="text-[#1F1812] text-[15px] mt-0.5"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Marlowe Hotel · Suite Redesign
        </p>
      </div>
      <span className="text-[10px] bg-[#F4ECE0] text-[#8B6D43] px-2.5 py-1 rounded-full font-medium tracking-wide">
        Auto-captured
      </span>
    </div>
    <div className="px-6 py-5 space-y-4">
      {[
        { t: 'Today, 14:22', label: 'Client approved lobby flooring', sub: 'Travertine 24×24 honed · email thread', tone: 'green' },
        { t: 'Yesterday', label: 'Spec change: Sconces → 2700K', sub: 'Confirmed in design review meeting', tone: 'amber' },
        { t: 'Jun 18', label: 'Vendor flag: 6w lead on hardware', sub: 'Recorded from Ardmore Brass call', tone: 'amber' },
        { t: 'Jun 14', label: 'Preference saved: matte finishes only', sub: 'From Mrs. Marlowe site walkthrough', tone: 'green' },
      ].map((row) => (
        <div key={row.label} className="flex gap-4">
          <p className="text-[#A69580] text-[11px] font-medium tracking-wide w-[64px] flex-shrink-0 pt-0.5">
            {row.t}
          </p>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${row.tone === 'green' ? 'bg-[#5B7B6A]' : 'bg-[#C49A57]'}`} />
              <p className="text-[#1F1812] text-[13.5px] font-medium leading-tight">{row.label}</p>
            </div>
            <p className="text-[#8B7355] text-[11.5px] mt-1">{row.sub}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const HowItWorksSection = () => {
  const sectionRef = React.useRef(null);
  const [activeStep, setActiveStep] = React.useState(0);

  React.useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const usable = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), usable);
      const progress = usable > 0 ? scrolled / usable : 0;
      const idx = Math.min(steps.length - 1, Math.floor(progress * steps.length));
      setActiveStep(idx);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="bg-[#F9F2EB] relative"
      style={{ minHeight: '230vh' }}
    >
      <div className="sticky top-0 min-h-screen flex items-center">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-12 items-center w-full py-20">
          {/* Left: copy + timeline */}
          <div>
            <p className="text-[#7A5A2E] text-[12px] tracking-[0.24em] uppercase mb-7">How it works</p>
            <h2
              className="text-[#1F1812] leading-[1.08] tracking-[-0.02em]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                fontSize: 'clamp(2.2rem, 3.6vw, 3.1rem)',
              }}
            >
              From scattered information to studio intelligence.
            </h2>

            <div className="mt-14">
              {steps.map((s, i) => {
                const isActive = i === activeStep;
                return (
                  <div
                    key={s.num}
                    className={`relative flex gap-5 transition-opacity duration-500 ${
                      isActive ? 'opacity-100' : 'opacity-40'
                    }`}
                  >
                    {/* marker + connecting line */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <span
                        className={`w-3 h-3 rounded-full border-2 transition-colors duration-500 ${
                          isActive ? 'bg-[#2A211B] border-[#2A211B]' : 'bg-[#F9F2EB] border-[#B8864B]'
                        }`}
                      />
                      {i < steps.length - 1 && <span className="w-px flex-1 bg-[#D8CBB8] my-1.5" />}
                    </div>

                    <span
                      className="text-[#B8864B] text-[15px] pt-[1px] w-7 flex-shrink-0"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                      {s.num}
                    </span>

                    <div className="pb-12">
                      <h3 className="text-[#1F1812] text-[16px] font-semibold tracking-tight">{s.title}</h3>
                      <p className="mt-2.5 text-[#6B5E4F] text-[14.5px] leading-[1.7] max-w-[320px]">{s.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: visual swaps per active step */}
          <div className="relative min-h-[560px] flex items-center justify-center">
            {activeStep === 0 && (
              <img
                key="integrations"
                src={INTEGRATIONS_IMG}
                alt="Archen™ connects to the tools your team already uses"
                data-testid="how-step-integrations"
                className="w-full max-w-[440px] mx-auto animate-[fadeIn_0.6s_ease-out_both,float_5s_ease-in-out_infinite]"
              />
            )}
            {activeStep === 1 && (
              <div key="capture" className="w-full animate-[fadeIn_0.6s_ease-out_both]">
                <CaptureMock />
              </div>
            )}
            {activeStep === 2 && (
              <div key="ask" className="w-full animate-[fadeIn_0.6s_ease-out_both]">
                <img
                  src={ASK_ARCHEN_IMG}
                  alt="Ask Archen™ answering what the client approved for the kitchen countertops, with cited email, design approval, and material spec sources"
                  data-testid="how-step-ask"
                  className="w-full rounded-2xl border border-white/60 ring-1 ring-black/[0.04] shadow-[0_44px_100px_-30px_rgba(42,32,24,0.32)]"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>
    </section>
  );
};

export default HowItWorksSection;
